import type { Breadcrumb, Event } from '@sentry/core';
import { z } from 'zod';

/**
 * Web routes whose path carries a bearer token: whoever has the URL can act for its owner. They
 * stay off analytics (`apps/web/src/root.tsx`), and monitoring sees the path with the token
 * replaced. A new token route belongs here.
 */
export const BEARER_TOKEN_ROUTE_PREFIXES = [
  // Ande Line booker onboarding: files a card and enrolls the booker's phone (72 h invite).
  '/ande-line/onboard/',
  // Ande Line payment recovery: files the booker's default card (15 min).
  '/ande-line/payment/',
  // Ande Line venue contact onboarding: makes a phone the venue's request contact (72 h invite).
  '/ande-line/venue-onboard/',
  // Ande Line venue responses: accepts, declines or quotes a booking request (#11501).
  '/ande-line-requests/',
  // Ande Agent card links: files the booker's default card (AGI-20).
  '/agent-card/',
  // Venue contracts: signs the contract, sets gratuity, starts the deposit (no expiry).
  '/contract/',
  // SOW review: downloads the statement of work (no expiry).
  '/sow-review/',
  // SMS file links: downloads a file shared in a venue agent chat (no expiry).
  '/f/',
  // SMS short links: opens a venue file the agent texted to a guest (HOS-772, no expiry).
  '/s/',
  // Venue agent take-over from the escalation email: stops the agent on one guest's thread (72 h).
  '/venue-agent-takeover/',
] as const;

export const isBearerTokenPath = (pathname: string): boolean =>
  BEARER_TOKEN_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));

const escapeRegExp = (text: string) => text.replaceAll(/[.*+?^${}()|[\]\\]/gu, String.raw`\$&`);

const TOKEN_IN_PATH = new RegExp(
  `(${BEARER_TOKEN_ROUTE_PREFIXES.map(escapeRegExp).join('|')})[^/?#\\s"']+`,
  'gu',
);

export const REDACTED_TOKEN = '[token]';

/** A URL, path or message with every bearer-token route's token replaced. */
export const redactBearerTokens = (text: string): string =>
  text.replaceAll(TOKEN_IN_PATH, `$1${REDACTED_TOKEN}`);

/** Deep enough for request data, `extra` and `contexts`; Sentry normalizes deeper values away. */
const MAX_DEPTH = 10;

const textSchema = z.string();

/**
 * Plain data only: events and breadcrumbs can hold live objects (a console breadcrumb's arguments,
 * request objects), which must be neither read through nor written to.
 */
const isPlainData = <Value extends object>(value: Value): boolean => {
  const prototype: unknown = Object.getPrototypeOf(value);

  return Array.isArray(value) || prototype === Object.prototype || prototype === null;
};

/**
 * Replaces the token in every string held in plain data anywhere in `container` (headers such as
 * Referer, query strings, request data, `extra`, `contexts`, exception values, spans,
 * breadcrumbs), in place. Each object is visited once and only changed strings are written.
 */
const redactNested = <Container extends object>(
  container: Container,
  depth = 0,
  seen = new WeakSet<object>(),
): void => {
  if (depth > MAX_DEPTH || seen.has(container) || !isPlainData(container)) {
    return;
  }
  seen.add(container);
  const changed: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(container)) {
    const text = textSchema.safeParse(value);
    if (text.success) {
      const redacted = redactBearerTokens(text.data);
      if (redacted !== text.data) {
        changed.push([key, redacted]);
      }
    } else if (value instanceof Object) {
      redactNested(value, depth + 1, seen);
    }
  }
  if (changed.length > 0) {
    Object.assign(container, Object.fromEntries(changed));
  }
};

/** Navigation and fetch breadcrumbs carry the page or request URL. */
export const redactBearerTokenBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  redactNested(breadcrumb);

  return breadcrumb;
};

/** Every string in an error or transaction event, wherever a bearer-token path appears in it. */
export const redactBearerTokenEvent = <SentryEvent extends Event>(
  event: SentryEvent,
): SentryEvent => {
  redactNested(event);

  return event;
};

/** Analytics payloads (a PostHog capture, a gtag hit): every string with its token replaced. */
export const redactBearerTokenData = <Data extends object>(data: Data): Data => {
  redactNested(data);

  return data;
};
